/**
 * SchoolResetPasswordPage — landing page reached via the per-school password
 * reset email link: /school/{tenantId}/reset-password?token=…&email=…&type=…
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { GraduationCap, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolResetPasswordPage = () => {
  const { tenantId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const email = params.get('email') || '';
  const userType = params.get('type') || 'teacher';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/school/tenants/${tenantId}/public`)
      .then(r => r.ok ? r.json() : null)
      .then(setTenant)
      .catch(() => {});
  }, [tenantId]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/school/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId, email, user_type: userType, token, new_password: password }),
      });
      // Iter-382: handle non-JSON error responses gracefully. Some 500s
      // (proxy timeout, raw "Internal Server Error", HTML error pages) are
      // not valid JSON — `res.json()` then throws the cryptic
      // `Unexpected token 'I', "Internal S"...` users were seeing. Read as
      // text first and parse defensively.
      const raw = await res.text();
      let body = {};
      try { body = raw ? JSON.parse(raw) : {}; } catch (_e) {
        body = { detail: raw && raw.length < 200 ? raw : 'Server error. Please try again or request a new reset link.' };
      }
      if (!res.ok) throw new Error(body.detail || body.message || `Reset failed (${res.status})`);
      setDone(true);
      toast.success('Password updated. You can now log in.');
    } catch (err) {
      toast.error(err.message || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-indigo-50">
        <Card className="max-w-md"><CardContent className="py-8 text-center">
          <p className="text-sm text-slate-700">Invalid or missing reset link. Please request a new password reset from the login page.</p>
          <Button className="mt-4" onClick={() => navigate(`/school/${tenantId}/login`)}>Go to login</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url.startsWith('http') ? tenant.logo_url : `${API_URL}${tenant.logo_url}`} alt="" className="h-16 w-auto mx-auto mb-3 rounded-lg object-contain bg-white p-1 shadow-sm" />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center mx-auto mb-3">
              <GraduationCap className="h-7 w-7 text-white" />
            </div>
          )}
          <h1 className="text-xl font-bold text-slate-900">{tenant?.name || 'School Portal'}</h1>
          <p className="text-sm text-slate-500 mt-1">Reset your password</p>
        </div>
        <Card><CardContent className="py-6">
          {done ? (
            <div className="text-center space-y-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
              <p className="text-sm">Your password has been updated.</p>
              <Button className="w-full" onClick={() => navigate(`/school/${tenantId}/login?type=${userType}`)} data-testid="reset-done-login-btn">
                Log in now
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="school-reset-form">
              <div className="text-xs text-slate-600 bg-slate-50 rounded px-3 py-2">Resetting password for <span className="font-medium">{email}</span> ({userType})</div>
              <div>
                <Label>New password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="reset-new-password" />
              </div>
              <div>
                <Label>Confirm new password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-password" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Update password
              </Button>
            </form>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
};

export default SchoolResetPasswordPage;
